import type { NavigateFunction } from 'react-router-dom';
import type { useSubmitForm } from './use-registration-api';
import { getFingerprint } from '@/utils/fingerprint';
import { invalidateIdentityCache } from '@/core/identity-check';

type Deps = {
  answers: (string | null)[];
  submitForm: ReturnType<typeof useSubmitForm>;
  setSessionToken: (token: string, expiresAt: string) => void;
  navigate: NavigateFunction;
};

type SubmitRes = {
  blocked?: boolean;
  reason?: string;
  retry_at?: string;
  passed?: boolean;
  session_token?: string;
  expires_at?: string;
};

function routeSubmit(res: SubmitRes, deps: Deps): void {
  const { setSessionToken, navigate } = deps;
  if (res.blocked) {
    invalidateIdentityCache();
    navigate('/', { replace: true });
    return;
  }
  if (!res.passed) invalidateIdentityCache();
  if (res.session_token && res.expires_at) {
    setSessionToken(res.session_token, res.expires_at);
  }
  navigate('/register/result', {
    state: { passed: !!res.passed, retryAt: res.retry_at },
  });
}

export function useFormSubmit(deps: Deps): () => Promise<void> {
  return async (): Promise<void> => {
    const fp = await getFingerprint();
    const res = await deps.submitForm.mutateAsync({
      answers: deps.answers as string[],
      identity: { fingerprint: fp },
    });
    routeSubmit(res, deps);
  };
}
